import React from 'react';
import '../../styles/PlatformBreakdown.css';

/**
 * PlatformBreakdown Component
 *
 * Shows revenue breakdown by booking platform
 * Highlights direct bookings (0% commission) advantage
 */
const PlatformBreakdown = ({ platforms, loading, hideHeader = false }) => {
  if (loading) {
    return (
      <div className="platform-breakdown loading" dir="rtl">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">جاري التحميل...</span>
        </div>
      </div>
    );
  }

  if (!platforms || platforms.length === 0) {
    return (
      <div className="platform-breakdown" dir="rtl">
        <div className="no-data">لا توجد بيانات متاحة</div>
      </div>
    );
  }

  const formatNumber = (num) => {
    return num?.toLocaleString('en-US', { maximumFractionDigits: 2 }) || '0';
  };

  const getPlatformIcon = (platform) => {
    const iconMap = {
      'airbnb': 'bi-house-heart',
      'booking': 'bi-building',
      'expedia': 'bi-airplane',
      'gathern': 'bi-geo-alt',
      'almosafer': 'bi-compass',
      'direct': 'bi-star-fill',
      'other': 'bi-globe'
    };
    return iconMap[platform?.toLowerCase()] || 'bi-globe';
  };

  const getPlatformLogo = (platform) => {
    const logoMap = {
      'airbnb': '/images/airbnb.svg',
      'booking': '/images/booking.com.svg',
      'gathern': '/images/GatherN.png',
      'direct': '/images/Host Me logo 2.png'
    };
    return logoMap[platform?.toLowerCase()] || null;
  };

  const getPlatformDisplayName = (platform) => {
    const nameMap = {
      'airbnb': 'Airbnb',
      'booking': 'Booking.com',
      'expedia': 'Expedia',
      'gathern': 'Gathern',
      'almosafer': 'Almosafer',
      'direct': 'حجز مباشر',
      'other': 'أخرى'
    };
    return nameMap[platform?.toLowerCase()] || platform;
  };

  // Sort platforms: Direct first, then by revenue (for table display)
  const sortedPlatforms = [...platforms].sort((a, b) => {
    if (a.platform === 'direct') return -1;
    if (b.platform === 'direct') return 1;
    return (b.grossRevenue || 0) - (a.grossRevenue || 0);
  });

  // Best platform by NET revenue (not forced Direct first)
  const bestPlatform = [...platforms].sort((a, b) => {
    return (b.netRevenue || 0) - (a.netRevenue || 0);
  })[0];

  // Calculate totals
  const totals = platforms.reduce((acc, p) => ({
    bookings: acc.bookings + (p.bookings || 0),
    gross: acc.gross + (p.grossRevenue || 0),
    commission: acc.commission + (p.commission || 0),
    net: acc.net + (p.netRevenue || 0)
  }), { bookings: 0, gross: 0, commission: 0, net: 0 });

  return (
    <div className="platform-breakdown" dir="rtl">
      {!hideHeader && (
        <div className="section-header">
          <h3>
            <i className="bi bi-diagram-3"></i>
            أداء المنصات - توزيع الحجوزات حسب المنصة
          </h3>
        </div>
      )}

      {/* Desktop Table View */}
      <div className="platforms-table-wrapper">
        <table className="platforms-table">
          <thead>
            <tr>
              <th>المنصة</th>
              <th>الحجوزات</th>
              <th>المبلغ الإجمالي</th>
              <th>عمولة المنصة</th>
              <th>نسبة العمولة</th>
              <th>الصافي للمالك</th>
            </tr>
          </thead>
          <tbody>
            {sortedPlatforms.map((platform, index) => {
              const isDirect = platform.platform === 'direct';
              return (
                <tr key={index} className={isDirect ? 'direct-row' : ''}>
                  <td>
                    <div className="platform-name">
                      {getPlatformLogo(platform.platform) ? (
                        <img
                          src={getPlatformLogo(platform.platform)}
                          alt={platform.platform}
                          style={{height: '24px', width: 'auto', flexShrink: 0}}
                        />
                      ) : (
                        <i className={`bi ${getPlatformIcon(platform.platform)}`}></i>
                      )}
                      <span>{getPlatformDisplayName(platform.platform)}</span>
                      {isDirect && <span className="badge-direct">(هوست مي)</span>}
                    </div>
                  </td>
                  <td className="text-center">
                    <span className="booking-count">{platform.bookings || 0}</span>
                  </td>
                  <td className="amount">
                    {formatNumber(platform.grossRevenue)} ر.س
                  </td>
                  <td className="commission">
                    {formatNumber(platform.commission)} ر.س
                  </td>
                  <td className="commission-rate">
                    <span className={isDirect ? 'badge-success' : ''}>
                      {formatNumber(platform.commissionRate)}%
                    </span>
                  </td>
                  <td className="net-amount">
                    <strong>{formatNumber(platform.netRevenue)} ر.س</strong>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="totals-row">
              <td><strong>الإجمالي</strong></td>
              <td className="text-center"><strong>{totals.bookings}</strong></td>
              <td className="amount"><strong>{formatNumber(totals.gross)} ر.س</strong></td>
              <td className="commission"><strong>{formatNumber(totals.commission)} ر.س</strong></td>
              <td>-</td>
              <td className="net-amount"><strong>{formatNumber(totals.net)} ر.س</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="platforms-cards">
        {sortedPlatforms.map((platform, index) => {
          const isDirect = platform.platform === 'direct';
          return (
            <div key={index} className={`platform-card ${isDirect ? 'direct-card' : ''}`}>
              <div className="card-header">
                <div className="platform-info">
                  {getPlatformLogo(platform.platform) ? (
                    <img
                      src={getPlatformLogo(platform.platform)}
                      alt={platform.platform}
                      className="platform-logo"
                    />
                  ) : (
                    <i className={`bi ${getPlatformIcon(platform.platform)}`}></i>
                  )}
                  <div className="platform-text">
                    <strong>{getPlatformDisplayName(platform.platform)}</strong>
                    {isDirect && <span className="badge-direct">(هوست مي)</span>}
                  </div>
                </div>
              </div>

              <div className="card-body">
                <div className="card-row">
                  <span className="label">الحجوزات</span>
                  <span className="value booking-number">{platform.bookings || 0}</span>
                </div>
                <div className="card-row">
                  <span className="label">المبلغ الإجمالي</span>
                  <span className="value amount">{formatNumber(platform.grossRevenue)} ر.س</span>
                </div>
                <div className="card-row">
                  <span className="label">عمولة المنصة</span>
                  <span className="value commission">{formatNumber(platform.commission)} ر.س</span>
                </div>
                <div className="card-row">
                  <span className="label">نسبة العمولة</span>
                  <span className={`value ${isDirect ? 'badge-success' : ''}`}>
                    {formatNumber(platform.commissionRate)}%
                  </span>
                </div>
                <div className="card-row highlight">
                  <span className="label">الصافي للمالك</span>
                  <span className="value net-amount">
                    <strong>{formatNumber(platform.netRevenue)} ر.س</strong>
                  </span>
                </div>
              </div>
            </div>
          );
        })}

        {/* Mobile Totals Card */}
        <div className="platform-card totals-card">
          <div className="card-header">
            <div className="platform-info">
              <i className="bi bi-calculator"></i>
              <div className="platform-text">
                <strong>الإجمالي</strong>
              </div>
            </div>
          </div>

          <div className="card-body">
            <div className="card-row">
              <span className="label">الحجوزات</span>
              <span className="value booking-number"><strong>{totals.bookings}</strong></span>
            </div>
            <div className="card-row">
              <span className="label">المبلغ الإجمالي</span>
              <span className="value amount"><strong>{formatNumber(totals.gross)} ر.س</strong></span>
            </div>
            <div className="card-row">
              <span className="label">عمولة المنصة</span>
              <span className="value commission"><strong>{formatNumber(totals.commission)} ر.س</strong></span>
            </div>
            <div className="card-row highlight">
              <span className="label">الصافي للمالك</span>
              <span className="value net-amount">
                <strong>{formatNumber(totals.net)} ر.س</strong>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Platform Performance Insights */}
      <div className="platform-insights">
        <div className="insight-card">
          <i className="bi bi-trophy"></i>
          <div>
            <strong>الأفضل أداءً (الصافي)</strong>
            <p>{getPlatformDisplayName(bestPlatform?.platform)} ({formatNumber(bestPlatform?.netRevenue)} ر.س)</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformBreakdown;
